import {
  LIVE_REPORTING_VERSION,
  type LiveReportPinRequest,
  type LiveReportResult,
  type LiveReportSummary,
  type LiveReportSummaryPage,
} from 'src/queries/liveReporting/liveReporting.models'
import {
  MAX_REPORTING_ID_LENGTH,
  reportingIdentifier,
} from 'src/queries/reporting/reporting.identifier'
import { parseReportingSnapshot } from 'src/queries/reporting/reporting.validation'

export const MAX_LIVE_REPORT_ID_LENGTH = MAX_REPORTING_ID_LENGTH

const object = (value: unknown, name: string): Record<string, unknown> => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${name} must be an object`)
  }
  return value as Record<string, unknown>
}

const exact = (
  value: Record<string, unknown>,
  keys: readonly string[],
  name: string,
): void => {
  const allowed = new Set(keys)
  const unknown = Object.keys(value).filter((key) => !allowed.has(key))
  if (unknown.length > 0) {
    throw new Error(`${name} has unknown fields: ${unknown.join(', ')}`)
  }
}

const string = (value: unknown, name: string): string => {
  if (typeof value !== 'string') throw new Error(`${name} must be a string`)
  return value
}

const nonEmptyString = (value: unknown, name: string): string => {
  const result = string(value, name)
  if (result.length === 0 || result.trim() !== result) {
    throw new Error(`${name} must be a non-empty trimmed string`)
  }
  return result
}

const title = (value: unknown, name: string): string => {
  const result = nonEmptyString(value, name)
  if (result.length > 256)
    throw new Error(`${name} must be at most 256 characters`)
  return result
}

const version = (value: unknown, name: string): void => {
  if (value !== LIVE_REPORTING_VERSION) {
    throw new Error(`${name}.version is unsupported`)
  }
}

const isoTimestamp = (value: unknown, name: string): string => {
  const result = nonEmptyString(value, name)
  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z$/.test(result) ||
    Number.isNaN(Date.parse(result)) ||
    new Date(result).toISOString().slice(0, 19) !== result.slice(0, 19)
  ) {
    throw new Error(`${name} must be a canonical UTC timestamp`)
  }
  return result
}

const isoDate = (value: unknown, name: string): string => {
  const result = nonEmptyString(value, name)
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(result) ||
    Number.isNaN(Date.parse(`${result}T00:00:00Z`)) ||
    new Date(`${result}T00:00:00Z`).toISOString().slice(0, 10) !== result
  ) {
    throw new Error(`${name} must be a canonical ISO date`)
  }
  return result
}

const id = (value: unknown, name: string): string => {
  return reportingIdentifier(value, name)
}

const parseSummary = (value: unknown, index: number): LiveReportSummary => {
  const source = object(value, `live report.items[${index}]`)
  exact(
    source,
    ['version', 'id', 'candidate_id', 'title', 'created_at', 'updated_at'],
    `live report.items[${index}]`,
  )
  version(source.version, `live report.items[${index}]`)
  return {
    version: LIVE_REPORTING_VERSION,
    id: id(source.id, `live report.items[${index}].id`),
    candidate_id: id(
      source.candidate_id,
      `live report.items[${index}].candidate_id`,
    ),
    title: title(source.title, `live report.items[${index}].title`),
    created_at: isoTimestamp(
      source.created_at,
      `live report.items[${index}].created_at`,
    ),
    updated_at: isoTimestamp(
      source.updated_at,
      `live report.items[${index}].updated_at`,
    ),
  }
}

export const parseLiveReportPinRequest = (
  value: unknown,
): LiveReportPinRequest => {
  const source = object(value, 'live report pin')
  exact(source, ['version', 'candidate_id', 'title'], 'live report pin')
  version(source.version, 'live report pin')
  return {
    version: LIVE_REPORTING_VERSION,
    candidate_id: id(source.candidate_id, 'live report pin.candidate_id'),
    title: title(source.title, 'live report pin.title'),
  }
}

export const parseLiveReportSummaryPage = (
  value: unknown,
): LiveReportSummaryPage => {
  const source = object(value, 'live reports')
  exact(source, ['version', 'items', 'next_cursor'], 'live reports')
  version(source.version, 'live reports')
  if (!Array.isArray(source.items))
    throw new Error('live reports.items must be an array')
  if (source.items.length > 12) throw new Error('live reports.items exceeds 12')
  const nextCursor =
    source.next_cursor === null
      ? null
      : id(source.next_cursor, 'live reports.next_cursor')
  return {
    version: LIVE_REPORTING_VERSION,
    items: source.items.map(parseSummary),
    next_cursor: nextCursor,
  }
}

export const parseLiveReportSummary = (value: unknown): LiveReportSummary =>
  parseSummary(value, 0)

export const parseLiveReportDetail = parseLiveReportSummary

export const parseLiveReportResult = (value: unknown): LiveReportResult => {
  const source = object(value, 'live report result')
  exact(
    source,
    [
      'version',
      'report_id',
      'title',
      'chart_spec',
      'dataset',
      'resolved_range',
      'generated_at',
      'analysis_context_token',
    ],
    'live report result',
  )
  version(source.version, 'live report result')
  const snapshot = parseReportingSnapshot(source.chart_spec, source.dataset)
  const range = object(
    source.resolved_range,
    'live report result.resolved_range',
  )
  exact(range, ['start', 'end'], 'live report result.resolved_range')
  const resolvedRange = {
    start: isoDate(range.start, 'live report result.resolved_range.start'),
    end: isoDate(range.end, 'live report result.resolved_range.end'),
  }
  if (resolvedRange.start >= resolvedRange.end) {
    throw new Error('live report result.resolved_range must be increasing')
  }
  return {
    version: LIVE_REPORTING_VERSION,
    report_id: id(source.report_id, 'live report result.report_id'),
    title: title(source.title, 'live report result.title'),
    chart_spec: snapshot.chart_spec,
    dataset: snapshot.dataset,
    resolved_range: resolvedRange,
    generated_at: isoTimestamp(
      source.generated_at,
      'live report result.generated_at',
    ),
    analysis_context_token: nonEmptyString(
      source.analysis_context_token,
      'live report result.analysis_context_token',
    ),
  }
}
